import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisDialogForm from "../../components/formdialog";
import { api, toInputDate, JIS_START } from "./hctc_common";
import "./hctc.css";

// Register / edit a historical case (screen hist_register; verified records need hist_verify).
// The fields follow the case filing form, plus the historical identifiers of spec 2.3.
const BLANK = {
  id: 0, originalCaseNumber: "", legacySuitNumber: "", legacyCaseNumber: "", caseTitle: "", originalFilingDate: "",
  filingDateStatus: "", courtTypeId: 1, divisionId: "", judgeId: "", judgeName: "", registrarName: "", caseTypeId: "",
  caseCategoryId: "", caseSubCatId: "", caseOriginId: "", originalFilingRegistry: "", physicalFileNumber: "",
  archiveReferenceNumber: "", archiveLocation: "", recordSource: "", intakeChannel: "Registry Registration",
  availabilityStatus: "", notes: "",
};
const blankParty = (typeId) => ({ id: 0, partyTypeId: typeId, name: "", isOrg: false, isLead: false, alternativeNames: "", email: "", phone: "", address: "" });

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const str = (v) => (v === "" ? null : v);

export default function HistoricalCaseForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, frequentData, setIsExpired } = useContext(ShareContext);
  const [lk, setLk] = useState(null);
  const [c, setC] = useState(BLANK);
  const [parties, setParties] = useState([blankParty(Constants.ClaimantType), blankParty(Constants.DefendantType)]);
  const [claims, setClaims] = useState([{ id: 0, claim: "", claimValue: "" }]);
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [dups, setDups] = useState(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const l = await api(token, setIsExpired, "historical/lookups");
      if (l.code === 0) setLk(l);
      if (id) {
        const d = await api(token, setIsExpired, `historical/${id}`);
        if (d.code === 0) {
          const it = d.item;
          const row = {};
          Object.keys(BLANK).forEach((k) => (row[k] = it[k] ?? ""));
          row.originalFilingDate = toInputDate(it.originalFilingDate);
          setC(row);
          setParties(d.parties?.length ? d.parties.map((p) => ({ ...blankParty(p.partyTypeId), ...p, partyTypeId: p.partyTypeId ?? "" })) : [blankParty(Constants.ClaimantType)]);
          setClaims(d.claims?.length ? d.claims : [{ id: 0, claim: "", claimValue: "" }]);
        } else setErr(d.status || "Unable to load the record");
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!c.divisionId) {
      setJudges([]);
      return;
    }
    (async () => {
      try {
        const resp = await fetch(`${Constants.apiUrl}/profile/getjudges/${c.divisionId}`);
        const d = await resp.json();
        setJudges(d.list || []);
      } catch (e) {
        setJudges([]);
      }
    })();
  }, [c.divisionId]);

  const onC = (e) => {
    const { name, value } = e.target;
    const next = { ...c, [name]: value };
    if (name === "originalFilingDate" && value) next.filingDateStatus = "";
    if (name === "caseTypeId") next.caseCategoryId = "";
    if (name === "caseCategoryId") next.caseSubCatId = "";
    if (name === "judgeId") {
      const j = judges.find((x) => String(x.id) === String(value));
      next.judgeName = j ? Constants.judgeName(j) : c.judgeName;
    }
    setC(next);
  };

  const setParty = (i, k, v) => setParties(parties.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const setClaim = (i, k, v) => setClaims(claims.map((p, j) => (j === i ? { ...p, [k]: v } : p)));

  const dateWarn = c.originalFilingDate && c.originalFilingDate >= JIS_START;

  const save = async (force) => {
    setErr("");
    if (dateWarn) {
      setErr("The filing date is on or after 2 October 2013. That is a JIS case, not a historical case.");
      return;
    }
    const body = {
      case: {
        ...c,
        id: Number(c.id) || 0,
        originalFilingDate: str(c.originalFilingDate),
        filingDateStatus: str(c.filingDateStatus),
        courtTypeId: num(c.courtTypeId),
        divisionId: num(c.divisionId),
        judgeId: num(c.judgeId),
        caseTypeId: num(c.caseTypeId),
        caseCategoryId: num(c.caseCategoryId),
        caseSubCatId: num(c.caseSubCatId),
        caseOriginId: num(c.caseOriginId),
        availabilityStatus: str(c.availabilityStatus),
      },
      parties: parties.filter((p) => p.name?.trim()).map((p) => ({ ...p, id: p.id || 0, partyTypeId: num(p.partyTypeId) })),
      claims: claims.filter((x) => x.claim?.trim()).map((x) => ({ ...x, id: x.id || 0 })),
      force: !!force,
      comment: comment || null,
    };
    setLoading(true);
    const d = await api(token, setIsExpired, "historical/save", { method: "POST", body });
    setLoading(false);
    if (d.code === 0) navigate(`/historical-cases/${d.id}`);
    else if (d.code === -5) setDups(d.duplicates || []);
    else if (d.code !== -10) setErr(d.status || "Unable to save");
  };

  const categories = (frequentData?.casecategories || []).filter((x) => !c.caseTypeId || !x.caseTypeId || String(x.caseTypeId) === String(c.caseTypeId));
  const subcats = (frequentData?.subcats || []).filter((x) => String(x.caseCategoryId) === String(c.caseCategoryId));

  const F = ({ name, label, req, type = "text", hint, span }) => (
    <div className={`hctc-field ${span || ""}`}>
      <label>
        {label}
        {req && <span className="req">*</span>}
      </label>
      <input type={type} name={name} value={c[name] ?? ""} onChange={onC} />
      {hint && <div className="hint">{hint}</div>}
    </div>
  );

  return (
    <div className="hctc-page">
      <h2>{id ? "Edit Historical Case" : "Register Historical Case"}</h2>
      <div className="hctc-sub">
        For cases filed before 2 October 2013. Where something is not known, use Unknown / Not Available / Pending Verification. Such values are not treated as confirmed facts.
      </div>
      {err && <div className="hctc-err">{err}</div>}

      <div className="hctc-card">
        <div className="hctc-section-title">Case identification</div>
        <div className="hctc-grid">
          {F({ name: "originalCaseNumber", label: "Original case number", req: true, hint: "As assigned before 2 Oct 2013 (or give a legacy number)" })}
          {F({ name: "legacySuitNumber", label: "Legacy suit number" })}
          {F({ name: "legacyCaseNumber", label: "Legacy case number(s)", hint: "Separate several with commas" })}
          {F({ name: "caseTitle", label: "Case title", req: true, span: "span2" })}
          <div className="hctc-field">
            <label>
              Original filing date<span className="req">*</span>
            </label>
            <input type="date" name="originalFilingDate" value={c.originalFilingDate} max="2013-10-01" onChange={onC} />
            {dateWarn && <div className="hint" style={{ color: "#a12622" }}>On/after 2 Oct 2013 - not historical</div>}
          </div>
          {!c.originalFilingDate && (
            <div className="hctc-field">
              <label>
                Filing date not known<span className="req">*</span>
              </label>
              <select name="filingDateStatus" value={c.filingDateStatus} onChange={onC}>
                <option value="">Select</option>
                {(lk?.unknownValues || []).map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </div>
          )}
          <div className="hctc-field">
            <label>
              Court<span className="req">*</span>
            </label>
            <select name="courtTypeId" value={c.courtTypeId} onChange={onC}>
              {(lk?.courts || []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.title}
                </option>
              ))}
            </select>
          </div>
          <div className="hctc-field">
            <label>
              Judicial division<span className="req">*</span>
            </label>
            <select name="divisionId" value={c.divisionId} onChange={onC}>
              <option value="">Select</option>
              {frequentData?.divisions?.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </select>
          </div>
          <div className="hctc-field">
            <label>
              Record source<span className="req">*</span>
            </label>
            <select name="recordSource" value={c.recordSource} onChange={onC}>
              <option value="">Select</option>
              {(lk?.recordSources || []).map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </div>
          {!id && (
            <div className="hctc-field">
              <label>Intake channel</label>
              <select name="intakeChannel" value={c.intakeChannel} onChange={onC}>
                {(lk?.intakeChannels || []).map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="hctc-card">
        <div className="hctc-section-title">Case details (as on the case filing form)</div>
        <div className="hctc-grid">
          <div className="hctc-field">
            <label>Case type</label>
            <select name="caseTypeId" value={c.caseTypeId} onChange={onC}>
              <option value="">Not known</option>
              {frequentData?.casetype?.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.caseType1}
                </option>
              ))}
            </select>
            <div className="hint">Required before migrating to JIS</div>
          </div>
          <div className="hctc-field">
            <label>Case category</label>
            <select name="caseCategoryId" value={c.caseCategoryId} onChange={onC}>
              <option value="">Not known</option>
              {categories.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.categoryTitle}
                </option>
              ))}
            </select>
          </div>
          <div className="hctc-field">
            <label>Case sub category</label>
            <select name="caseSubCatId" value={c.caseSubCatId} onChange={onC} disabled={!subcats.length}>
              <option value="">Not known</option>
              {subcats.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.title}
                </option>
              ))}
            </select>
          </div>
          <div className="hctc-field">
            <label>Case origin</label>
            <select name="caseOriginId" value={c.caseOriginId} onChange={onC}>
              <option value="">Not known</option>
              {frequentData?.caseorigin?.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.title}
                </option>
              ))}
            </select>
          </div>
          <div className="hctc-field">
            <label>Judge (on JIS)</label>
            <select name="judgeId" value={c.judgeId} onChange={onC} disabled={!c.divisionId}>
              <option value="">Not on JIS / not known</option>
              {judges.map((j) => (
                <option key={j.id} value={j.id}>
                  {Constants.judgeName(j)}
                </option>
              ))}
            </select>
          </div>
          {F({ name: "judgeName", label: "Judge as written on the record" })}
          {F({ name: "registrarName", label: "Registrar" })}
          {F({ name: "originalFilingRegistry", label: "Original filing registry" })}
        </div>
      </div>

      <div className="hctc-card">
        <div className="hctc-section-title">Physical record</div>
        <div className="hctc-grid">
          {F({ name: "physicalFileNumber", label: "Physical file number" })}
          {F({ name: "archiveReferenceNumber", label: "Archive reference number" })}
          {F({ name: "archiveLocation", label: "Archive location", hint: "Room / shelf / box" })}
          <div className="hctc-field">
            <label>Availability</label>
            <select name="availabilityStatus" value={c.availabilityStatus} onChange={onC}>
              <option value="">Not assessed</option>
              {(lk?.availabilityStatuses || []).map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </div>
          <div className="hctc-field full">
            <label>Historical case notes</label>
            <textarea name="notes" value={c.notes} onChange={onC} />
          </div>
        </div>
      </div>

      <div className="hctc-card">
        <div className="head">
          <h3>Parties</h3>
          <JisButton variant="outline" onClick={() => setParties([...parties, blankParty(Constants.DefendantType)])}>
            Add party
          </JisButton>
        </div>
        {parties.map((p, i) => (
          <div key={i} className="hctc-grid" style={{ borderBottom: "1px dashed var(--border, #e2e8f0)", paddingBottom: 10, marginBottom: 10 }}>
            <div className="hctc-field">
              <label>Party type</label>
              <select value={p.partyTypeId ?? ""} onChange={(e) => setParty(i, "partyTypeId", e.target.value)}>
                <option value="">Select</option>
                {frequentData?.partyTypes?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="hctc-field span2">
              <label>Name (as on the record)</label>
              <input value={p.name} onChange={(e) => setParty(i, "name", e.target.value)} />
            </div>
            <div className="hctc-field">
              <label>Alternative spellings</label>
              <input value={p.alternativeNames || ""} onChange={(e) => setParty(i, "alternativeNames", e.target.value)} placeholder="comma separated" />
            </div>
            <div className="hctc-field">
              <label>Email</label>
              <input value={p.email || ""} onChange={(e) => setParty(i, "email", e.target.value)} />
            </div>
            <div className="hctc-field">
              <label>Phone</label>
              <input value={p.phone || ""} onChange={(e) => setParty(i, "phone", e.target.value)} />
            </div>
            <div className="hctc-field span2">
              <label>Address</label>
              <input value={p.address || ""} onChange={(e) => setParty(i, "address", e.target.value)} />
            </div>
            <div className="hctc-field" style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label className="hctc-check">
                <input type="checkbox" checked={!!p.isOrg} onChange={(e) => setParty(i, "isOrg", e.target.checked)} /> Organisation
              </label>
              <label className="hctc-check">
                <input type="checkbox" checked={!!p.isLead} onChange={(e) => setParty(i, "isLead", e.target.checked)} /> Lead party
              </label>
              <JisButton variant="plain" onClick={() => setParties(parties.filter((_, j) => j !== i))}>
                Remove
              </JisButton>
            </div>
          </div>
        ))}
      </div>

      <div className="hctc-card">
        <div className="head">
          <h3>Claims / reliefs</h3>
          <JisButton variant="outline" onClick={() => setClaims([...claims, { id: 0, claim: "", claimValue: "" }])}>
            Add claim
          </JisButton>
        </div>
        {claims.map((x, i) => (
          <div key={i} className="hctc-grid" style={{ marginBottom: 8 }}>
            <div className="hctc-field span2">
              <label>Claim</label>
              <input value={x.claim} onChange={(e) => setClaim(i, "claim", e.target.value)} />
            </div>
            <div className="hctc-field">
              <label>Value</label>
              <input value={x.claimValue || ""} onChange={(e) => setClaim(i, "claimValue", e.target.value)} />
            </div>
            <div className="hctc-field" style={{ display: "flex", alignItems: "flex-end" }}>
              <JisButton variant="plain" onClick={() => setClaims(claims.filter((_, j) => j !== i))}>
                Remove
              </JisButton>
            </div>
          </div>
        ))}
      </div>

      {id && (
        <div className="hctc-card">
          <div className="hctc-field">
            <label>Reason for the change (kept in the audit trail)</label>
            <input value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
        </div>
      )}

      <div className="hctc-actions">
        <JisButton onClick={() => save(false)}>Save</JisButton>
        <JisButton variant="outline" onClick={() => navigate(id ? `/historical-cases/${id}` : "/historical-cases")}>
          Cancel
        </JisButton>
      </div>

      {dups && (
        <JisDialogForm title="Possible duplicates" onClose={() => setDups(null)} minWidth="40%">
          <div className="hctc-dialog-body">
            <div className="hctc-warn">Records with the same case number already exist. Duplicates are never deleted; link them from the record instead.</div>
            <table className="case-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Number</th>
                  <th>Reference</th>
                  <th>Title</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dups.map((x) => (
                  <tr key={`${x.source}${x.id}`}>
                    <td>{x.source}</td>
                    <td>{x.number}</td>
                    <td>
                      {x.source === "Historical" ? (
                        <a href={`/historical-cases/${x.id}`} target="_blank" rel="noreferrer">
                          {x.historicalRef}
                        </a>
                      ) : (
                        x.historicalRef
                      )}
                    </td>
                    <td>{x.title}</td>
                    <td>{x.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="hctc-actions" style={{ marginTop: 12 }}>
              <JisButton variant="outline" onClick={() => setDups(null)}>
                Go back
              </JisButton>
              <JisButton
                onClick={() => {
                  setDups(null);
                  save(true);
                }}
              >
                Save anyway
              </JisButton>
            </div>
          </div>
        </JisDialogForm>
      )}
      <BackLoader open={loading} />
    </div>
  );
}
